/**
 * Connectors list page — `GET /connectors`.
 *
 * The connector *catalog*: the vendor integrations this deployment knows how to talk to. Rows are
 * not tenant-owned records, so there is nothing here to create, edit or delete — the verb is
 * "connect", which creates a connector *instance* (a configured, credentialed copy) on its own
 * wizard route.
 *
 * Migrated onto the CoOrdio list-page pattern (see `ProjectListPage.tsx`, the reference):
 * `ShellHeader`, `CordioAbpPagedDataTable`, and a `.connectors-list` BEM block. The grid owns
 * paging, search, sorting and filtering — all of which used to be page state (`searchTerm`,
 * `selectedCategory`, a client-side `filteredConnectors` memo and a `useEffect` that refetched on
 * category change) and none of which carried the test ids the E2E grid checklist selects on.
 *
 * There is no `listVersion` counter here, deliberately: the page performs no mutation, so nothing
 * would ever bump it. The pattern's one-piece-of-refetch-state rule has nothing to hold.
 *
 * WHY the page still windows results itself: `GET /connectors` is NOT paged. It accepts exactly two
 * filters — `category` and `isActive` — and returns a bare `ConnectorDto[]`; `searchTerm` is
 * matched inside `connectorService` against name / description / vendor. `pageNumber`, `pageSize`
 * and `authenticationType` exist on `ConnectorFilters` but the service never sends them, so this
 * page never sets them. {@link toClientPage} slices the response into the page the grid asked for.
 *
 * --- Column plan -------------------------------------------------------------------------------
 * field           | header       | width | cell type              | filter
 * ----------------|--------------|-------|------------------------|-----------------------------
 * name            | Connector    | 32%   | text, clickable        | toolbar search -> searchTerm
 * vendor          | Vendor       | 20%   | text                   | toolbar search -> searchTerm
 * category        | Category     | 20%   | custom (outlined Chip) | select `category` (loaded)
 * capabilityCount | Capabilities | 14%   | number                 | —
 * instanceCount   | Instances    | 14%   | number                 | —
 *
 * Dropped from the pre-migration screen:
 *   - The card / table view toggle and the `ConnectorCard` marketplace gallery. This is presentation
 *     rather than capability: every datum a card showed (name, vendor, category, capability count,
 *     instance count) is a column here, and both card buttons — View and Connect — are row actions.
 *     `ConnectorCard` is left in the tree unreferenced; deleting or re-siting it is a follow-up, not
 *     this migration's call.
 *   - The per-row `ConnectorIcon`. A 32px logo inside a grid cell fights the row height and breaks
 *     the clickable-name styling; the icon still identifies the connector on its detail page.
 *   - `authenticationType`. It matters when you configure an instance, not when you scan the
 *     catalog, and the connector detail page states it before you commit to a credential.
 *   - The "Browse All" header button, which navigated to this very page.
 *
 * Status tabs map to `isActive`, which the endpoint does accept. The pre-migration page hard-coded
 * `isActive: true` and never let the user look at retired connectors; All is now reachable.
 * -----------------------------------------------------------------------------------------------
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Chip,
  CordioAbpPagedDataTable,
  ShellHeader,
  type CordioDataTableAction,
  type CordioDataTableColumn,
  type CordioDataTableListToolbarConfig,
} from '@coordio/ui';

import {
  createCortexPagedDataSource,
  type CortexPagedQuery,
} from '../components/data-display/cortexPagedDataSource';
import { connectorService } from '../services/connectorService';
import { ROUTES } from '../utils/routes';

import type { ConnectorDto, ConnectorFilters } from '../types/connector';
import type { PagedResult } from '../types/tenant';

import './ConnectorsListPage.scss';

/**
 * Quick-filter tabs.
 *
 * `value` is load-bearing twice: the grid writes it to the toolbar's `paramKey`, AND it builds each
 * tab's test id as `cordio-datatable-status-tab-<value>`. GRID-13 asserts on `-all` / `-true` /
 * `-false`, so the All tab must carry the literal `'all'` — an empty value would emit
 * `cordio-datatable-status-tab-` and the checklist would select nothing. `'all'` is a UI concept the
 * API rejects, hence the synthetic {@link STATUS_TAB_PARAM_KEY} that {@link toConnectorFilters}
 * resolves to `isActive`.
 */
const STATUS_TAB_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Active', value: 'true' },
  { label: 'Inactive', value: 'false' },
] as const;

/** The synthetic toolbar key the status tabs write to. Never sent to the API as-is. */
const STATUS_TAB_PARAM_KEY = 'status';

/**
 * Turns the grid's query into the `ConnectorFilters` shape `connectorService.getConnectors` reads.
 *
 * Only `category`, `isActive` and `searchTerm` survive: those are the three the service acts on
 * (the first two as query-string parameters, the third as its own client-side match). The synthetic
 * status tab becomes `isActive`, and All becomes "no filter at all" rather than a third state.
 */
function toConnectorFilters(query: CortexPagedQuery): ConnectorFilters {
  const filters: ConnectorFilters = {};

  const status = query[STATUS_TAB_PARAM_KEY];
  if (status === 'true' || status === 'false') {
    filters.isActive = status === 'true';
  }

  if (typeof query.category === 'string' && query.category !== '') {
    filters.category = query.category;
  }

  if (typeof query.searchTerm === 'string' && query.searchTerm.trim() !== '') {
    filters.searchTerm = query.searchTerm;
  }

  return filters;
}

/**
 * Wraps an unpaged array response as the one page the grid asked for.
 *
 * `totalCount` is exact — `GET /connectors` returns the whole (filtered) catalog, so the client
 * knows the true total even though the endpoint never states one.
 */
function toClientPage<T>(items: readonly T[], pageNumber: number, pageSize: number): PagedResult<T> {
  const start = Math.max(0, (pageNumber - 1) * pageSize);
  return {
    items: items.slice(start, start + pageSize) as T[],
    totalCount: items.length,
    pageNumber,
    pageSize,
    totalPages: Math.max(1, Math.ceil(items.length / pageSize)),
    hasPreviousPage: pageNumber > 1,
    hasNextPage: start + pageSize < items.length,
  };
}

/**
 * Loads the category filter's options from the catalog itself.
 *
 * Categories are data, not an enum — the pre-migration page derived its chip row from whatever the
 * loaded connectors happened to declare, and a hard-coded list would silently omit a new one. The
 * grid runs this once, the first time the filter popover opens, and keeps the result.
 */
async function loadCategoryOptions(): Promise<ReadonlyArray<{ label: string; value: string }>> {
  const connectors = await connectorService.getConnectors();
  const categories = new Set<string>();
  for (const connector of connectors) {
    if (connector.category) categories.add(connector.category);
  }
  return [...categories].sort().map((category) => ({ label: category, value: category }));
}

export const ConnectorsListPage = () => {
  const navigate = useNavigate();

  const dataSource = useMemo(
    () =>
      createCortexPagedDataSource<ConnectorDto>(async (query) => {
        const items = await connectorService.getConnectors(toConnectorFilters(query));
        return toClientPage(items, query.pageNumber, query.pageSize);
      }),
    [],
  );

  const columns = useMemo<ReadonlyArray<CordioDataTableColumn<ConnectorDto>>>(
    () => [
      { field: 'name', header: 'Connector', type: 'text', width: '32%', sortable: true, clickable: true },
      { field: 'vendor', header: 'Vendor', type: 'text', width: '20%', sortable: true },
      {
        field: 'category',
        header: 'Category',
        type: 'custom',
        width: '20%',
        // Outlined rather than filled: a category is a label, not a state, and a filled chip in
        // every row would compete with the status chips elsewhere in the product.
        customCell: (row) => (row.category ? <Chip label={row.category} variant="outlined" /> : null),
      },
      { field: 'capabilityCount', header: 'Capabilities', type: 'number', width: '14%' },
      { field: 'instanceCount', header: 'Instances', type: 'number', width: '14%' },
    ],
    [],
  );

  const listToolbar = useMemo<CordioDataTableListToolbarConfig>(
    () => ({
      search: { placeholder: 'Search connectors by name, vendor or description' },
      statusTabs: {
        paramKey: STATUS_TAB_PARAM_KEY,
        ariaLabel: 'Connector status filter',
        options: [...STATUS_TAB_OPTIONS],
      },
      filters: [
        {
          key: 'category',
          label: 'Category',
          type: 'select',
          loadOptions: loadCategoryOptions,
        },
      ],
      filterButtonLabel: 'Filter',
    }),
    [],
  );

  const actions = useMemo<ReadonlyArray<CordioDataTableAction<ConnectorDto>>>(
    () => [
      {
        icon: 'eye',
        tooltip: 'View connector',
        onClick: (row) => navigate(`${ROUTES.CONNECTORS}/${row.id}`),
      },
      {
        icon: 'plug',
        tooltip: 'Connect',
        // Only offered for connectors that are still active: the wizard would create an instance of
        // an integration this deployment has retired.
        visible: (row) => row.isActive,
        onClick: (row) => navigate(ROUTES.CONNECTOR_INSTANCE_NEW.replace(':connectorId', row.id)),
      },
    ],
    [navigate],
  );

  return (
    <div className="connectors-list" data-testid="connector-list-page">
      <ShellHeader
        title="Connectors"
        description="The integrations this deployment can talk to. Connect one to create a credentialed instance your agents can call."
        data-testid="connector-list-header"
      />

      <div data-testid="connector-list-grid">
        <CordioAbpPagedDataTable<ConnectorDto>
          className="connectors-list__table"
          columns={columns}
          dataSource={dataSource}
          initialPageSize={25}
          maxBodyHeight="520px"
          listToolbar={listToolbar}
          actions={actions}
          onRowClick={(row) => navigate(`${ROUTES.CONNECTORS}/${row.id}`)}
        />
      </div>
    </div>
  );
};
